import { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, RotateCcw, SkipForward, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  resolveElement,
  groundStateFill,
  fillsToNotation,
  nobleGasShorthand,
  toSuperscript,
  FULL_BY_Z,
  type FullElement,
  type SubshellFill,
} from './atomicData';
import {
  VizCard,
  NowYouTry,
  VIZ_BTN_PRIMARY,
  VIZ_BTN_GHOST,
  VIZ_FOCUS,
  cueIsActive,
  cueHighlights,
  usePrefersReducedMotion,
  type VizCue,
} from '../vizShared';

// ElectronConfiguration — interactive aufbau builder.
//   • given config.element / config.symbol / config.Z (defaults to carbon),
//     fills orbitals one electron at a time following the diagonal (Madelung)
//     rule with a play / pause / step / reset transport.
//   • shows the orbital-box diagram (↑↓ arrows in s/p/d/f boxes obeying Hund's
//     rule), the live spdf notation, and the noble-gas shorthand.
//   • the user can change the element (± or jump) and re-run.
//   • self-demo on mount: auto-plays the aufbau fill ONCE from 0, then settles
//     full and shows "Now you try". Optional `cue` supersedes the demo:
//     step / progress drive electrons placed, play drives the player, highlight
//     picks an element by symbol/Z.
// Resilient: clamps Z to 1–118, never crashes on partial config.

export interface ElectronConfigurationConfig {
  title?: string;
  element?: string | number;
  symbol?: string;
  Z?: number;
  autoPlay?: boolean;
}

interface Props {
  config?: ElectronConfigurationConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// A single orbital box holds up to 2 electrons (one ↑, one ↓). A subshell has
// (cap/2) boxes: s→1, p→3, d→5, f→7.
const BOXES_PER_SUB: Record<string, number> = { s: 1, p: 3, d: 5, f: 7 };

const SUB_COLOR: Record<string, string> = {
  s: '#5B8FF9',
  p: '#5AD8A6',
  d: '#F6BD16',
  f: '#E8684A',
};

// Distribute `count` electrons across the boxes of a subshell obeying Hund's
// rule: fill every box with one spin-up first, then pair up with spin-down.
function distributeHund(count: number, boxes: number): Array<{ up: boolean; down: boolean }> {
  const slots = Array.from({ length: boxes }, () => ({ up: false, down: false }));
  let n = count;
  for (let i = 0; i < boxes && n > 0; i++) {
    slots[i].up = true;
    n--;
  }
  for (let i = 0; i < boxes && n > 0; i++) {
    slots[i].down = true;
    n--;
  }
  return slots;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

export default function ElectronConfiguration({ config = {}, onComplete, cue }: Props) {
  const initial =
    resolveElement(config.element ?? config.symbol ?? config.Z) ?? FULL_BY_Z[6]; // carbon
  const reducedMotion = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const [z, setZ] = useState(() => clamp(initial.z, 1, 118));
  // How many electrons have been "placed" by the animation (0..z).
  // Demo auto-plays from 0; reduced motion / cue render full immediately.
  const demoEnabled = !reducedMotion && !hasCue;
  const [placed, setPlaced] = useState(() => (demoEnabled || config.autoPlay ? 0 : initial.z));
  const [playing, setPlaying] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [demoDone, setDemoDone] = useState(!demoEnabled);
  const acc = useRef(0);
  const demoActiveRef = useRef(false);

  const el: FullElement = FULL_BY_Z[z] ?? initial;
  const { fills: groundFills, anomalous } = useMemo(() => groundStateFill(z), [z]);

  // Self-demo / autoplay: auto-fill once on mount (unless reduced motion / cue).
  useEffect(() => {
    if (demoEnabled || config.autoPlay) {
      setPlaced(0);
      setPlaying(true);
      demoActiveRef.current = demoEnabled;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Animation: advance ~6 electrons/sec while playing.
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      acc.current += dt;
      const step = 1 / 6;
      while (acc.current >= step) {
        acc.current -= step;
        setPlaced((p) => {
          if (p >= z) {
            setPlaying(false);
            if (demoActiveRef.current) {
              demoActiveRef.current = false;
              setDemoDone(true);
            }
            return z;
          }
          return p + 1;
        });
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, z]);

  const cancelDemo = () => {
    if (demoActiveRef.current) {
      demoActiveRef.current = false;
      setDemoDone(true);
    }
  };

  // ── Cue handling (supersedes self-demo) ──
  const cueTokens = useMemo(() => cueHighlights(cue), [cue]);
  // highlight → pick an element by symbol / Z / name.
  const lastHighlight = useRef<string | null>(null);
  useEffect(() => {
    const token = cueTokens[0];
    if (!token) return;
    if (token === lastHighlight.current) return;
    lastHighlight.current = token;
    const resolved = resolveElement(token);
    if (resolved) {
      cancelDemo();
      const c = clamp(resolved.z, 1, 118);
      setZ(c);
      setPlaced(c);
      setPlaying(false);
      acc.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cueTokens]);

  // step → set electrons placed (clamped); progress (0..1) → round(progress*z).
  useEffect(() => {
    if (!cue) return;
    if (cue.step != null) {
      cancelDemo();
      setPlaying(false);
      setPlaced(clamp(Math.round(cue.step), 0, z));
    } else if (cue.progress != null) {
      cancelDemo();
      setPlaying(false);
      setPlaced(clamp(Math.round(cue.progress * z), 0, z));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.step, cue?.progress, z]);

  // play → drive the player.
  useEffect(() => {
    if (cue?.play == null) return;
    cancelDemo();
    if (cue.play) {
      setPlaced((p) => (p >= z ? 0 : p));
      setPlaying(true);
    } else {
      setPlaying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.play, z]);

  const markInteracted = () => {
    if (!interacted) {
      setInteracted(true);
      onComplete?.({ type: 'electron_configuration', z });
    }
  };

  const setElement = (newZ: number) => {
    cancelDemo();
    const c = clamp(newZ, 1, 118);
    setZ(c);
    setPlaced(c); // show the full config immediately; user can replay
    setPlaying(false);
    acc.current = 0;
    markInteracted();
  };

  const play = () => {
    cancelDemo();
    if (placed >= z) setPlaced(0);
    setPlaying(true);
    markInteracted();
  };
  const pause = () => {
    cancelDemo();
    setPlaying(false);
    markInteracted();
  };
  const step = () => {
    cancelDemo();
    setPlaying(false);
    setPlaced((p) => clamp(p + 1, 0, z));
    markInteracted();
  };
  const reset = () => {
    cancelDemo();
    setPlaying(false);
    setPlaced(0);
    acc.current = 0;
    markInteracted();
  };

  // Build the PARTIAL fills shown so far (first `placed` electrons), in aufbau
  // order. We always animate via strict aufbau; once fully placed and the
  // element is anomalous, we display the corrected ground state.
  const shownFills: SubshellFill[] = useMemo(() => {
    if (placed >= z) return groundFills; // show true ground state when complete
    // Partial aufbau fill of `placed` electrons.
    return groundStateFillPartial(placed);
  }, [placed, z, groundFills]);

  const notation = fillsToNotation(shownFills);
  const { core, valence } = nobleGasShorthand(z);
  const complete = placed >= z;

  return (
    <VizCard
      title={config.title ?? 'Electron configuration'}
      eyebrow="Aufbau builder"
      subtitle="Watch orbitals fill · change the element and replay"
      hint="Play / Step through the diagonal (Madelung) rule · ± to change element"
      reducedMotion={reducedMotion}
    >
      <div className="relative flex w-full flex-col gap-3">
        <NowYouTry show={demoDone && demoEnabled && !hasCue} />

        {/* Element header + picker */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-ws-ink">{el.symbol}</span>
            <span className="text-sm text-ws-ink-2">{el.name}</span>
            <span className="text-xs text-ws-ink-3">Z = {z}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setElement(z - 1)}
              disabled={z <= 1}
              className={`rounded-sm border border-ws-border p-1 text-ws-ink-2 transition-colors hover:bg-ws-surface-2 disabled:opacity-40 ${VIZ_FOCUS}`}
              aria-label="Previous element"
            >
              <ChevronLeft size={14} />
            </button>
            <input
              type="number"
              min={1}
              max={118}
              value={z}
              onChange={(ev) => setElement(Number(ev.target.value) || 1)}
              className={`w-14 rounded-sm border border-ws-border bg-ws-surface px-1.5 py-1 text-center text-sm text-ws-ink tabular-nums ${VIZ_FOCUS}`}
              aria-label="Atomic number"
            />
            <button
              type="button"
              onClick={() => setElement(z + 1)}
              disabled={z >= 118}
              className={`rounded-sm border border-ws-border p-1 text-ws-ink-2 transition-colors hover:bg-ws-surface-2 disabled:opacity-40 ${VIZ_FOCUS}`}
              aria-label="Next element"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Notation readouts */}
        <div className="rounded-md border border-ws-border bg-ws-surface-2 p-2.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[10px] uppercase tracking-wide text-ws-ink-3">spdf</span>
            <span className="font-mono text-sm text-ws-ink">{notation || '—'}</span>
          </div>
          {complete && (
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-[10px] uppercase tracking-wide text-ws-ink-3">shorthand</span>
              <span className="font-mono text-sm text-ws-ink">
                {core ? `${core} ` : ''}
                {valence}
              </span>
            </div>
          )}
          {complete && anomalous && (
            <p className="mt-1 text-[11px] text-ws-warning">
              Anomalous configuration: the real ground state deviates from strict
              aufbau (extra stability from a half- or fully-filled d subshell).
            </p>
          )}
          <p className="mt-1 text-[11px] text-ws-ink-3">
            {placed} / {z} electrons placed
          </p>
        </div>

        {/* Orbital-box diagram */}
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          {shownFills.map((f) => (
            <SubshellBoxes key={f.label} fill={f} reducedMotion={reducedMotion} />
          ))}
          {shownFills.length === 0 && (
            <span className="text-xs text-ws-ink-3">Press play to fill orbitals.</span>
          )}
        </div>

        {/* Transport */}
        <div className="flex items-center gap-2">
          {playing ? (
            <button type="button" onClick={pause} className={VIZ_BTN_PRIMARY}>
              <Pause size={13} /> Pause
            </button>
          ) : (
            <button type="button" onClick={play} className={VIZ_BTN_PRIMARY}>
              <Play size={13} /> {complete ? 'Replay' : 'Play'}
            </button>
          )}
          <button type="button" onClick={step} disabled={complete} className={VIZ_BTN_GHOST}>
            <SkipForward size={13} /> Step
          </button>
          <button type="button" onClick={reset} className={VIZ_BTN_GHOST}>
            <RotateCcw size={13} /> Reset
          </button>
        </div>
      </div>
    </VizCard>
  );
}

// Strict-aufbau partial fill of exactly `count` electrons (for animation).
function groundStateFillPartial(count: number): SubshellFill[] {
  const cap: Record<string, number> = { s: 2, p: 6, d: 10, f: 14 };
  const order = groundStateFillPartialOrder;
  const fills: SubshellFill[] = [];
  let remaining = count;
  for (const { n, sub } of order) {
    if (remaining <= 0) break;
    const c = Math.min(cap[sub], remaining);
    fills.push({ n, sub, count: c, label: `${n}${sub}` });
    remaining -= c;
  }
  return fills;
}

// Local copy of the aufbau order (kept in sync with atomicData.AUFBAU_ORDER).
const groundStateFillPartialOrder: Array<{ n: number; sub: 's' | 'p' | 'd' | 'f' }> = [
  { n: 1, sub: 's' },
  { n: 2, sub: 's' }, { n: 2, sub: 'p' },
  { n: 3, sub: 's' }, { n: 3, sub: 'p' },
  { n: 4, sub: 's' }, { n: 3, sub: 'd' }, { n: 4, sub: 'p' },
  { n: 5, sub: 's' }, { n: 4, sub: 'd' }, { n: 5, sub: 'p' },
  { n: 6, sub: 's' }, { n: 4, sub: 'f' }, { n: 5, sub: 'd' }, { n: 6, sub: 'p' },
  { n: 7, sub: 's' }, { n: 5, sub: 'f' }, { n: 6, sub: 'd' }, { n: 7, sub: 'p' },
];

function SubshellBoxes({ fill, reducedMotion }: { fill: SubshellFill; reducedMotion: boolean }) {
  const boxes = BOXES_PER_SUB[fill.sub] ?? 1;
  const slots = distributeHund(fill.count, boxes);
  const color = SUB_COLOR[fill.sub] ?? '#94A3B8';
  // Ease each subshell in as it appears (opacity + slight scale).
  const [shown, setShown] = useState(reducedMotion);
  useEffect(() => {
    if (reducedMotion) {
      setShown(true);
      return;
    }
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [reducedMotion]);
  return (
    <div
      className="flex flex-col items-center gap-1"
      style={
        reducedMotion
          ? undefined
          : {
              opacity: shown ? 1 : 0,
              transform: shown ? 'scale(1)' : 'scale(0.85)',
              transition: 'opacity 260ms ease-out, transform 260ms ease-out',
            }
      }
    >
      <div className="flex gap-0.5">
        {slots.map((s, i) => (
          <div
            key={i}
            className="flex h-7 w-5 items-center justify-center rounded-[2px] border"
            style={{ borderColor: color }}
          >
            <span className="text-[11px] leading-none" style={{ color }}>
              {s.up && s.down ? '⇅' : s.up ? '↑' : s.down ? '↓' : ''}
            </span>
          </div>
        ))}
      </div>
      <span className="font-mono text-[10px] text-ws-ink-2">
        {fill.label}
        {toSuperscript(fill.count)}
      </span>
    </div>
  );
}
